import { can } from '@clim/client-core';
import { OPEN_INTERVENTION, SAV_OPEN, commissionTotals, formatEuros, isLateIntervention, isoDate } from '@clim/domain';
import { Link } from 'react-router-dom';
import { isAdminArea } from '../components/Layout';
import { MyDocsBanner } from '../components/ContractorDocs';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';

interface Tile {
  to: string;
  label: string;
  icon: string;
  tone: string;
  count?: number;
  hint?: string;
}

/** Hub d'accueil à tuiles, adapté dynamiquement aux rôles (CDC §3). */
export function HomePage() {
  const engine = useEngine();
  const { me } = useReady();
  const { territoryId } = useTerritory();
  const dossiers = engine.list('dossier').filter((d) => !territoryId || d.territoryId === territoryId);
  const mine = dossiers.filter((d) => d.assignments.some((a) => a.userId === me.user.id && a.role === 'commercial'));
  const toFix = mine.filter((d) => d.status === 'to_correct').length;
  const toControl = dossiers.filter((d) => d.status === 'submitted').length;
  const isCommercial = can(me, 'dossier.create');

  const sales: Tile[] = [];
  const other0: Tile[] = [];
  if (isCommercial) {
    sales.push({ to: '/dossiers/nouveau', label: 'Nouveau dossier', icon: '＋', tone: 'vente' });
    sales.push({ to: '/dossiers?vue=mes', label: 'Mes dossiers', icon: '📁', tone: 'vente', count: mine.length, hint: toFix ? `${toFix} à corriger` : undefined });
  }
  const field: Tile[] = [];
  if (can(me, 'intervention.execute')) {
    const mineIv = engine.list('intervention').filter((i) => i.technicianId === me.user.id);
    const toAccept = mineIv.filter((i) => i.status === 'proposed').length;
    const today = isoDate(new Date());
    const late = mineIv.filter((i) => isLateIntervention(i, today)).length;
    const hint = [late ? `⚠️ ${late} en retard` : null, toAccept ? `${toAccept} à accepter` : null].filter(Boolean).join(' · ');
    field.push({ to: '/interventions', label: 'Mes interventions', icon: '🔧', tone: 'pose', count: mineIv.filter((i) => OPEN_INTERVENTION.includes(i.status) && i.status !== 'impossible').length, hint: hint || undefined });
  }
  // prospects : le commercial voit ce qu'il a à appeler ; le superviseur suit l'équipe
  if (can(me, 'prospect.read') && !can(me, 'prospect.manage')) {
    const todo = engine.prospectsToContact;
    sales.push({ to: '/prospects', label: 'Mes prospects', icon: '☎', tone: 'vente', count: todo, hint: todo ? 'à appeler' : undefined });
  }
  const control: Tile[] = [];
  if (can(me, 'prospect.manage')) {
    const open = engine.list('prospect').filter((p) => (!territoryId || p.territoryId === territoryId) && p.status !== 'archived');
    const unassigned = open.filter((p) => !p.assignedToId).length;
    control.push({ to: '/prospects', label: 'Prospects', icon: '☎', tone: 'admin', count: open.filter((p) => p.status === 'to_contact' || p.status === 'callback').length, hint: unassigned ? `${unassigned} non attribué(s)` : 'en cours' });
  }
  if (can(me, 'planning.manage')) {
    const toAssign = engine.list('intervention').filter((i) => (!territoryId || i.territoryId === territoryId) && ['to_assign', 'impossible'].includes(i.status)).length;
    control.push({ to: '/planning', label: 'Planning', icon: '🗓', tone: 'pose', count: dossiers.filter((d) => d.status === 'to_schedule').length, hint: toAssign ? `${toAssign} à attribuer` : 'dossiers à planifier' });
  } else if (can(me, 'planning.read')) other0.push({ to: '/planning', label: 'Planning (vue d’ensemble)', icon: '🗓', tone: 'neutral' });
  if (can(me, 'dossier.control')) control.push({ to: '/dossiers?statut=submitted', label: 'Dossiers à contrôler', icon: '✔', tone: 'admin', count: toControl });
  if (can(me, 'dossier.schedule')) control.push({ to: '/dossiers?statut=validated', label: 'Validés à planifier', icon: '📅', tone: 'admin', count: dossiers.filter((d) => d.status === 'validated').length });
  if (can(me, 'payment.cash.confirm')) {
    const toValidate = engine.list('payment').filter((p) => p.methodKind !== 'stripe' && ['to_remit', 'pending'].includes(p.status) && (!territoryId || p.territoryId === territoryId));
    control.push({ to: '/encaissements', label: 'Encaissements à valider', icon: '💶', tone: 'admin', count: toValidate.length, hint: toValidate.length ? formatEuros(toValidate.reduce((s, p) => s + p.amountCents, 0)) : undefined });
  }
  if (can(me, 'commission.read')) {
    const month = new Date().toISOString().slice(0, 7);
    const mineC = engine.list('commission').filter((c) => c.beneficiaryId === me.user.id && c.period === month);
    const total = commissionTotals(mineC).totalCents;
    const tile = { to: '/commissions', label: me.roles.includes('technicien') && !me.roles.includes('commercial') ? 'Mes rémunérations' : 'Mes commissions', icon: '€', tone: 'vente', count: undefined, hint: `${formatEuros(total)} ce mois` };
    if (isCommercial) sales.push(tile);
    else if (can(me, 'intervention.execute')) field.push({ ...tile, tone: 'pose' });
    if (me.permissions['commission.read'] === 'territory' || me.permissions['commission.read'] === 'all') {
      const open = engine.list('commission').filter((c) => c.status === 'to_validate' && (!territoryId || c.territoryId === territoryId));
      control.push({ to: '/commissions/equipe', label: 'Commissions équipe', icon: '€', tone: 'admin', count: open.length, hint: open.length ? 'à valider' : undefined });
    }
  }
  if (can(me, 'sav.read')) {
    const savs = engine.list('sav').filter((s) => !territoryId || s.territoryId === territoryId);
    const toValidate = savs.filter((s) => s.status === 'pending_validation').length;
    const openCount = savs.filter((s) => SAV_OPEN.includes(s.status)).length;
    const tile = { to: '/sav', label: 'SAV', icon: '🛠', tone: 'sav', count: openCount, hint: toValidate && can(me, 'sav.validate') ? `${toValidate} à valider` : undefined };
    if (can(me, 'sav.validate')) control.push(tile);
    else other0.push({ ...tile, tone: 'neutral' });
  }
  if (can(me, 'stock.read')) control.push({ to: '/stock', label: 'Stock et matériel', icon: '📦', tone: 'admin', hint: 'niveaux, matériel du jour' });
  if (can(me, 'stats.read')) control.unshift({ to: '/tableau-de-bord', label: 'Tableau de bord', icon: '📈', tone: 'admin', hint: 'indicateurs, activité du jour' });
  if (can(me, 'invoice.sync')) control.push({ to: '/facturation', label: 'Facturation', icon: '🧾', tone: 'admin', hint: 'file « À facturer »' });
  if (can(me, 'review.request')) control.push({ to: '/avis', label: 'Avis clients', icon: '⭐', tone: 'admin' });
  if (can(me, 'stats.read')) control.push({ to: '/qualite', label: 'Qualité', icon: '📊', tone: 'admin' });
  if (can(me, 'contractor_document.read') || can(me, 'contractor_document.manage')) control.push({ to: '/sous-traitants', label: 'Documents sous-traitants', icon: '📄', tone: 'admin' });
  if (me.user.isContractor) other0.push({ to: '/mes-documents', label: 'Mes documents', icon: '📄', tone: 'neutral' });
  // §3 / §15 : « Ressources & Annonces » pour tous, badge « non lu »
  const unreadNews = engine.unreadAnnouncements;
  other0.unshift({ to: '/annonces', label: 'Ressources & Annonces', icon: '📣', tone: unreadNews ? 'sav' : 'neutral', count: unreadNews || undefined, hint: unreadNews ? 'non lue(s)' : undefined });
  const other: Tile[] = [...other0];
  if (can(me, 'client.read')) other.push({ to: '/clients', label: 'Clients', icon: '👤', tone: 'neutral' });
  other.push({ to: '/sync', label: 'Synchronisation', icon: '⟳', tone: 'neutral', count: engine.pendingCount + engine.pendingUploads || undefined, hint: engine.issues.length ? `${engine.issues.length} alerte(s)` : undefined });
  if (isAdminArea(me)) other.push({ to: '/admin', label: 'Équipe & paramètres', icon: '⚙', tone: 'admin' });
  other.push({ to: '/securite', label: 'Sécurité du compte', icon: '🔒', tone: 'neutral', hint: me.user.mfaEnabled ? 'double vérification active' : undefined });

  const section = (title: string, tiles: Tile[]) =>
    tiles.length ? (
      <section className="hub-section" key={title}>
        <h2>{title}</h2>
        <div className="hub">
          {tiles.map((t) => (
            <Link key={t.label} to={t.to} className={`tile tile-${t.tone}`}>
              <span className="tile-icon" aria-hidden>
                {t.icon}
              </span>
              <span className="tile-label">{t.label}</span>
              {t.count !== undefined && <span className="tile-count">{t.count}</span>}
              {t.hint && <span className="tile-hint">{t.hint}</span>}
            </Link>
          ))}
        </div>
      </section>
    ) : null;

  return (
    <div>
      <h1>Bonjour {me.user.firstName}</h1>
      {me.user.isContractor && <MyDocsBanner />}
      <UrgentBanner />
      {/* Profil mixte : combiné mais visuellement séparé (CDC §2) */}
      {section('Ventes', sales)}
      {section('Terrain', field)}
      {section('Contrôle', control)}
      {section('Général', other)}
    </div>
  );
}

/** Annonces urgentes non lues, en tête de l'accueil (§15). */
function UrgentBanner() {
  const engine = useEngine();
  const { me } = useReady();
  const urgent = engine.liveAnnouncements().filter((a) => a.urgent && !a.readAt && a.publishedById !== me.user.id);
  if (!urgent.length) return null;
  return (
    <div className="card legal-alert" role="alert" aria-label="Annonces urgentes">
      {urgent.map((a) => (
        <div key={a.id}>
          <strong>Urgent — {a.title}</strong>{' '}
          <Link to={`/annonces#${a.id}`}>Lire</Link>
        </div>
      ))}
    </div>
  );
}
